import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Rocket, ShieldCheck, Sparkles, X, Loader2 } from 'lucide-react';
import { setSandboxMeta } from '../hooks/useSandboxSession';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TIERS = [
  {
    id: 'Standard',
    icon: ShieldCheck,
    title: 'Standard License',
    blurb: 'Core GST billing — invoices, quotations, payments, customers, products.',
    bullets: ['3 user seats', 'Sales & purchase flows', 'GST reports', '24-hour sandbox'],
  },
  {
    id: 'Pro',
    icon: Sparkles,
    title: 'Pro License',
    blurb: 'Full feature set — E-Invoicing, E-Way Bills, Inventory, Multi-currency, GST Compliance.',
    bullets: ['10 user seats', 'E-Invoicing + E-Way Bills', 'Inventory & Multi-currency', '24-hour sandbox'],
    recommended: true,
  },
];

/**
 * DemoSandboxModal — tier-picker dialog shown when the visitor clicks
 * "Explore Live Demo" on the AuthPage.
 *
 * Mobile-responsive (single-column stack on small screens, 2-up grid
 * on md+). On selection it calls POST /api/v1/create-ephemeral-sandbox,
 * persists the returned JWT into localStorage, stores sandbox metadata
 * so the rest of the app can detect demo mode, and navigates to /dashboard.
 */
export function DemoSandboxModal({ open, onOpenChange, accentColor = '#f59e0b', goldColor = '#f59e0b' }) {
  const [busyTier, setBusyTier] = useState(null);

  if (!open) return null;

  const launch = async (tier) => {
    setBusyTier(tier);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/v1/create-ephemeral-sandbox`, {
        requested_tier: tier,
      });
      const { access_token, organization_id, expires_at } = res.data || {};
      if (!access_token) throw new Error('Sandbox response missing access_token');
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setSandboxMeta({ tier, organization_id, expires_at });
      toast.success(`Launching ${tier} sandbox — your workspace is ready for 24 hours.`);
      // Full reload so AuthContext picks up the new token via /api/auth/me
      window.location.href = '/dashboard';
    } catch (err) {
      const d = err?.response?.data?.detail || err?.message || 'Could not launch demo sandbox';
      toast.error(typeof d === 'string' ? d : 'Demo unavailable. Please try again.');
      setBusyTier(null);
    }
  };

  return (
    <div
      data-testid="demo-sandbox-modal-overlay"
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-slate-900/75 backdrop-blur-sm"
      onClick={() => !busyTier && onOpenChange(false)}
    >
      <div
        data-testid="demo-sandbox-modal"
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-3xl bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[92vh] overflow-y-auto"
      >
        {/* Header */}
        <div
          className="relative px-6 py-5 text-white"
          style={{ background: `linear-gradient(135deg, #0f172a 0%, ${goldColor} 180%)` }}
        >
          <button
            type="button"
            onClick={() => !busyTier && onOpenChange(false)}
            data-testid="demo-sandbox-modal-close"
            className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center transition"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-3">
            <div
              className="w-11 h-11 rounded-xl flex items-center justify-center"
              style={{ background: `${goldColor}33`, border: `1px solid ${goldColor}88` }}
            >
              <Rocket className="w-5 h-5" style={{ color: goldColor }} />
            </div>
            <div>
              <h2 className="text-xl font-bold leading-tight">Select Your Demo Environment</h2>
              <p className="text-sm text-white/75 mt-0.5">
                Pre-loaded with sample products, customers & vendors. No sign-up required.
              </p>
            </div>
          </div>
        </div>

        {/* Tier grid */}
        <div className="p-6 grid md:grid-cols-2 gap-4">
          {TIERS.map(({ id, icon: Icon, title, blurb, bullets, recommended }) => {
            const busy = busyTier === id;
            const disabled = !!busyTier;
            return (
              <div
                key={id}
                role="button"
                tabIndex={disabled ? -1 : 0}
                onClick={() => !disabled && launch(id)}
                onKeyDown={(e) => {
                  if (disabled) return;
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    launch(id);
                  }
                }}
                aria-disabled={disabled}
                data-testid={`demo-sandbox-tier-${id.toLowerCase()}`}
                className={`relative text-left rounded-2xl border-2 p-5 transition-all cursor-pointer hover:shadow-lg active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-amber-400 ${
                  recommended ? 'border-transparent' : 'border-slate-200 hover:border-slate-300'
                } ${disabled ? 'opacity-60 pointer-events-none' : ''}`}
                style={
                  recommended
                    ? { borderColor: goldColor, background: `${goldColor}0d` }
                    : undefined
                }
              >
                {recommended && (
                  <span
                    className="absolute -top-2.5 right-4 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full text-white"
                    style={{ background: goldColor }}
                  >
                    Recommended
                  </span>
                )}
                <div className="flex items-center gap-3 mb-3">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ background: `${accentColor}1a`, color: accentColor }}
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="text-base font-bold text-slate-900">{title}</h3>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed mb-3">{blurb}</p>
                <ul className="space-y-1.5 mb-4">
                  {bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-xs text-slate-700">
                      <span
                        className="mt-0.5 w-1.5 h-1.5 rounded-full flex-shrink-0"
                        style={{ background: goldColor }}
                      />
                      {b}
                    </li>
                  ))}
                </ul>
                <div
                  className="w-full text-white font-semibold inline-flex items-center justify-center rounded-md h-10 px-4"
                  style={{ background: recommended ? goldColor : '#0f172a' }}
                  data-testid={`demo-sandbox-launch-${id.toLowerCase()}`}
                >
                  {busy ? (
                    <span className="inline-flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Launching…
                    </span>
                  ) : (
                    `Launch ${id} Sandbox`
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <p className="px-6 pb-6 text-[11px] text-slate-500 text-center">
          Sandbox tenants are wiped automatically 24 hours after launch.
        </p>
      </div>
    </div>
  );
}

export default DemoSandboxModal;
